import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { characters } from "@/mocks/characters";
import { episodes } from "@/mocks/episodes";
import { useFavorites } from "@/hooks/useFavorites";

const roleStyle: Record<string, string> = {
  主角: "bg-primary-500 text-background-50",
  配角: "bg-secondary-500 text-background-50",
  反派: "bg-foreground-900 text-background-50",
};

export default function CharacterDetail() {
  const { id } = useParams<{ id: string }>();
  const character = characters.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!character) {
    return (
      <div className="rounded-lg bg-background-50 border border-background-200 p-12 text-center">
        <title>该角色信息不存在 - 甜蜜惩罚动漫</title>
        <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400 mb-4">
          <i className="ri-user-unfollow-line"></i>
        </span>
        <h1 className="font-heading text-xl text-foreground-950 mb-2">该角色信息不存在</h1>
        <p className="text-sm text-foreground-500 font-label mb-6">
          这位角色可能尚未收录，返回角色介绍页看看其他人物吧。
        </p>
        <Link
          to="/characters"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> 返回角色介绍
        </Link>
      </div>
    );
  }

  const saved = isFavorite("character", character.id);
  const appearances = episodes.filter((episode) => character.appearances.includes(episode.id));

  const handleFavorite = () => {
    const nowSaved = toggleFavorite("character", character.id);
    setToast(nowSaved ? "已加入我的收藏" : "已取消收藏");
  };

  return (
    <div className="space-y-6">
      <title>{character.name}角色介绍 - 甜蜜惩罚动漫</title>
      <meta
        name="description"
        content={`甜蜜惩罚${character.role}「${character.name}」角色介绍：${character.tagline}。${character.description.slice(0, 60)}`}
      />
      <meta name="keywords" content={`甜蜜惩罚${character.name},${character.name}角色,甜蜜惩罚${character.role}`} />
      <link rel="canonical" href={`https://jdbykj.com/character/${character.id}`} />

      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: "角色介绍", to: "/characters" }, { label: character.name }]}
      />

      <article className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-[300px] shrink-0 bg-background-100">
            <div className="relative w-full aspect-[3/4] md:aspect-[3/4]">
              <img
                src={character.avatar}
                alt={`${character.name} 角色立绘`}
                title={`甜蜜惩罚 ${character.name} 角色立绘`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 p-6 md:p-8 min-w-0">
            <div className="flex items-center gap-3 mb-3">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">{character.name}</h1>
              <span className={`px-3 py-1 rounded-md text-sm font-label ${roleStyle[character.role]}`}>
                {character.role}
              </span>
            </div>
            <p className="text-base text-primary-700 font-label mb-5">{character.tagline}</p>

            <div className="flex flex-wrap gap-3 mb-6">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                  saved
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "bg-background-100 text-foreground-800 border border-background-300 hover:border-primary-300"
                }`}
              >
                <i className={saved ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                {saved ? "已收藏本角色" : "收藏本角色"}
              </button>
              <Link
                to="/characters"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-100 text-foreground-800 border border-background-300 text-sm font-label hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 返回角色介绍
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="rounded-md bg-background-100 p-4">
                <p className="text-xs text-foreground-500 font-label mb-1.5">角色定位</p>
                <p className="text-sm text-foreground-800 font-label">{character.role} · 第 {character.appearanceOrder} 位出场</p>
              </div>
              <div className="rounded-md bg-background-100 p-4">
                <p className="text-xs text-foreground-500 font-label mb-1.5">出场集数</p>
                <p className="text-sm text-foreground-800 font-label">共 {appearances.length} 集</p>
              </div>
            </div>

            <section className="mb-6">
              <h2 className="font-heading text-lg text-foreground-950 mb-2.5 flex items-center gap-2">
                <i className="ri-profile-line text-primary-600"></i>角色简介
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-loose font-label">{character.description}</p>
            </section>

            <section>
              <h2 className="font-heading text-lg text-foreground-950 mb-2.5 flex items-center gap-2">
                <i className="ri-emotion-line text-primary-600"></i>性格分析
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-loose font-label">{character.personality}</p>
            </section>
          </div>
        </div>
      </article>

      <section className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
        <h2 className="font-heading text-xl text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-film-line text-accent-600"></i>所属剧情（出场集数）
        </h2>
        <div className="flex flex-wrap gap-2">
          {appearances.map((episode) => (
            <Link
              key={episode.id}
              to={`/episode/${episode.id}`}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-background-100 border border-background-300 text-sm font-label text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <span className="px-1.5 py-0.5 rounded bg-primary-100 text-primary-800 text-xs">第 {episode.number} 集</span>
              {episode.title}
            </Link>
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-label animate-fade-up">
          <i className="ri-checkbox-circle-line mr-1.5 text-accent-400"></i>
          {toast}
        </div>
      )}
    </div>
  );
}